import { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

// ============================================================================
// CHA-272 — live AZURE SYNC chip source
// ============================================================================
// Read-only subscription on the existing `azure_sync_status` collection
// (auth-read allowed by rules; written by scheduledAzureSync). The chip shows
// the most recent successful file sync in ET, and degrades to a warning when
// the newest success is older than 24h or any file carries a lastError.
const ET = 'America/New_York';
const STALE_HOURS = 24;

export const deriveSync = (docs, now = new Date()) => {
  const files = docs.filter((d) => d.id !== '_transform');
  const successTimes = files
    .filter((d) => d.status === 'success' && d.lastSync)
    .map((d) => new Date(d.lastSync))
    .filter((d) => !Number.isNaN(d.getTime()))
    .sort((a, b) => b - a);
  const latest = successTimes[0] || null;
  // Error detection is scoped to files that succeeded RECENTLY (48h): the
  // sync writes a sticky lastError (e.g. "File not found in Azure") without
  // changing the stored status, so a retired/seasonal file carries its error
  // forever. A file that synced fine yesterday and errors today is a real
  // problem; one whose last success is ancient is retired noise.
  const anyError = files.some((d) => {
    if (!d.lastError) return false;
    const lastGood = d.lastSync ? new Date(d.lastSync) : null;
    return (
      lastGood &&
      !Number.isNaN(lastGood.getTime()) &&
      now.getTime() - lastGood.getTime() <= 48 * 3600e3
    );
  });
  if (!latest) return { label: 'AZURE SYNC —', ok: false };
  const fresh = now.getTime() - latest.getTime() <= STALE_HOURS * 3600e3;
  const hhmm = latest.toLocaleTimeString('en-US', {
    timeZone: ET,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const ok = fresh && !anyError;
  return { label: `AZURE SYNC ${hhmm} ${ok ? '✓' : '⚠'}`, ok };
};

export default function useSyncStatus({ enabled = true } = {}) {
  const [docs, setDocs] = useState(null); // null = no snapshot yet
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setDocs([]);
      setFailed(false);
      return undefined;
    }
    const db = getFirestore();
    const unsub = onSnapshot(
      collection(db, 'azure_sync_status'),
      (snap) => {
        setFailed(false);
        setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        // Non-fatal chrome detail — never block the shell on it.
        console.error('shell: azure_sync_status subscription failed', err);
        setFailed(true);
      }
    );
    return unsub;
  }, [enabled]);

  // Hourly tick: freshness must decay with wall-clock time, not only when a
  // Firestore snapshot happens to arrive — a stalled scheduler emits nothing.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 3600e3);
    return () => clearInterval(id);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => {
    if (failed) return { label: 'AZURE SYNC —', ok: false };
    if (docs === null) return null; // pre-snapshot: TopBar renders neutral
    return deriveSync(docs);
  }, [docs, failed, tick]);
}
